import { ViewportType } from './types';

/**
 * Structural family a viewport belongs to. Every {@link ViewportType} maps to
 * exactly one category. Use this to reason about viewports by their broad kind
 * (3D volume / 2D slice / CPR) rather than enumerating individual types.
 */
export enum ViewportCategory {
    VOLUME_3D = '3d',
    SLICE_2D = 'slice',
    CPR = 'cpr',
}

/**
 * Single source of truth for viewport categorization.
 *
 * Typed as `Record<ViewportType, ViewportCategory>` on purpose: adding a new
 * member to {@link ViewportType} without classifying it here is a *compile*
 * error, not a silent runtime gap. Prefer the predicates below over reading
 * this map directly.
 */
const VIEWPORT_CATEGORY: Record<ViewportType, ViewportCategory> = {
    [ViewportType.VOLUME_3D]: ViewportCategory.VOLUME_3D,
    [ViewportType.SLICE_AXIAL]: ViewportCategory.SLICE_2D,
    [ViewportType.SLICE_SAGITTAL]: ViewportCategory.SLICE_2D,
    [ViewportType.SLICE_CORONAL]: ViewportCategory.SLICE_2D,
    [ViewportType.SLICE_OBLIQUE]: ViewportCategory.SLICE_2D,
    [ViewportType.CPR_STRETCHED]: ViewportCategory.CPR,
    [ViewportType.CPR_STRAIGHTENED]: ViewportCategory.CPR,
};

/** Resolve the {@link ViewportCategory} for a given viewport type. */
export function getViewportCategory(type: ViewportType): ViewportCategory {
    return VIEWPORT_CATEGORY[type];
}

// --- Structural predicates (the mutually-exclusive partition) ---------------
// All accept `undefined` so call sites can pass a possibly-unset `vp.type`
// without an extra guard.

/** A 3D volume viewport. */
export function is3DViewport(type?: ViewportType): boolean {
    return type !== undefined && VIEWPORT_CATEGORY[type] === ViewportCategory.VOLUME_3D;
}

/** A 2D slice viewport (axial, sagittal, coronal, or oblique). */
export function isSliceViewport(type?: ViewportType): boolean {
    return type !== undefined && VIEWPORT_CATEGORY[type] === ViewportCategory.SLICE_2D;
}

/** A CPR viewport (straightened or stretched). */
export function isCprViewport(type?: ViewportType): boolean {
    return type !== undefined && VIEWPORT_CATEGORY[type] === ViewportCategory.CPR;
}

// --- Capabilities (derived from the partition, never re-enumerated) ---------

/** 2D slices and CPR respond to window/level; the 3D volume does not. */
export function respondsToWindowLevel(type?: ViewportType): boolean {
    return isSliceViewport(type) || isCprViewport(type);
}
